import { Button, Input, Select, SelectItem } from '@nextui-org/react';
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from '@nextui-org/modal';
import React, { useRef, useState } from 'react';

import { SectionInfo } from '../types';
import { getUploadUrl, createResource } from '../services/resourceService';
import { useAuth0 } from '@auth0/auth0-react';

interface AddResourceModalProps {
  isOpen: boolean;
  onClose: () => void;
  sections: SectionInfo[];
  onSaved: () => void;
}

const AddResourceModal: React.FC<AddResourceModalProps> = ({
  isOpen,
  onClose,
  sections,
  onSaved,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [title, setTitle] = useState('');
  const [section, setSection] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [url, setUrl] = useState('');
  const [resourceType, setResourceType] = useState<'file' | 'link'>('file');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) {
      setFile(f);
      if (!title) setTitle(f.name.replace(/\.[^/.]+$/, ''));
    }
  };

  const getFileType = (filename: string): string => {
    return filename.split('.').pop()?.toLowerCase() || '';
  };

  const resetForm = () => {
    setTitle('');
    setSection('');
    setFile(null);
    setUrl('');
    setResourceType('file');
    setError(null);
  };

  const handleSave = async () => {
    if (!title || !section) {
      setError('Title and section are required.');
      return;
    }

    if (resourceType === 'file' && !file) {
      setError('Please select a file to upload.');
      return;
    }

    if (resourceType === 'link' && !url) {
      setError('Please enter a URL.');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const token = await getAccessTokenSilently();

      if (resourceType === 'file' && file) {
        // Upload file flow
        const { url: uploadUrl } = await getUploadUrl(token);
        const uploadRes = await fetch(uploadUrl, {
          method: 'POST',
          headers: { 'Content-Type': file.type },
          body: file,
        });
        if (!uploadRes.ok) throw new Error('File upload failed');
        const { storageId } = await uploadRes.json();

        await createResource(token, {
          title,
          filename: file.name,
          storageId,
          fileType: getFileType(file.name),
          section,
          displayOrder: 0,
          resourceType: 'file',
        });
      } else {
        // Link flow
        await createResource(token, {
          title,
          section,
          displayOrder: 0,
          resourceType: 'link',
          url,
        });
      }

      resetForm();
      onSaved();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to create resource');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={() => { resetForm(); onClose(); }} size="lg">
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1 pb-2">
          <h3 className="text-lg font-bold text-slate-800">Add Resource</h3>
          <p className="text-xs text-slate-400 font-normal">
            Upload a file or add a link to an external page
          </p>
        </ModalHeader>
        <ModalBody className="gap-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Type toggle */}
          <div className="flex gap-2">
            <Button
              size="sm"
              radius="sm"
              variant={resourceType === 'file' ? 'solid' : 'bordered'}
              className={resourceType === 'file'
                ? 'bg-airlock-purple text-white'
                : 'border-slate-300 text-slate-600'}
              onClick={() => setResourceType('file')}
            >
              File Upload
            </Button>
            <Button
              size="sm"
              radius="sm"
              variant={resourceType === 'link' ? 'solid' : 'bordered'}
              className={resourceType === 'link'
                ? 'bg-airlock-purple text-white'
                : 'border-slate-300 text-slate-600'}
              onClick={() => setResourceType('link')}
            >
              External Link
            </Button>
          </div>

          {resourceType === 'file' ? (
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.png,.jpg,.jpeg,.zip"
                onChange={handleFileChange}
                className="hidden"
              />
              <Button
                variant="bordered"
                radius="sm"
                className="w-full border-slate-300 text-slate-600"
                onClick={() => fileInputRef.current?.click()}
              >
                {file ? file.name : 'Choose File...'}
              </Button>
              {file && (
                <p className="text-xs text-slate-400 mt-1">
                  {(file.size / 1024).toFixed(0)} KB &middot; .{getFileType(file.name).toUpperCase()}
                </p>
              )}
            </div>
          ) : (
            <Input
              label="URL"
              placeholder="https://example.com/page"
              variant="bordered"
              radius="sm"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              classNames={{ label: 'text-slate-500 text-xs' }}
            />
          )}

          <Input
            label="Title"
            variant="bordered"
            radius="sm"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            classNames={{ label: 'text-slate-500 text-xs' }}
          />

          <Select
            label="Section"
            variant="bordered"
            radius="sm"
            selectedKeys={section ? [section] : []}
            onSelectionChange={(keys) => {
              const val = Array.from(keys)[0] as string;
              setSection(val);
            }}
            classNames={{ label: 'text-slate-500 text-xs' }}
          >
            {sections.map((s) => (
              <SelectItem key={s.name} value={s.name}>
                {s.name}
              </SelectItem>
            ))}
          </Select>
        </ModalBody>
        <ModalFooter className="pt-2">
          <Button variant="bordered" onClick={() => { resetForm(); onClose(); }} radius="sm" className="border-slate-300 text-slate-600">
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={handleSave}
            radius="sm"
            isLoading={isLoading}
            className="bg-airlock-purple font-medium"
          >
            {resourceType === 'file' ? 'Upload & Save' : 'Add Link'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AddResourceModal;
